import React, { useMemo } from 'react'
import { Fade, Grid } from '@mui/material'
import TablePanel from '../../Support/TablePanel'
import Tabs from '../../../shared/components/Tabs/Tabs'
import { DataTableSection } from '../../../shared/components/DataTable'
import useActivitiesDisplayName from '../../../shared/hooks/useDisplayNames/variants/useActivitiesDisplayName'
import { useTableDetailPanel } from '../../../shared/hooks/useTableDetailPanel'
import { useGetActivitiesQuery } from '../../../shared/queries/useGetActivitiesQuery'
import { useReorderActivitiesMutation } from '../../../shared/queries/useReorderActivitiesMutation'
import { useInvalidateActivitiesQuery } from '../../../shared/queries/useInvalidateActivitiesQuery'
import { useInvalidateGetValueMessageLinkedDataQuery } from '../../../shared/queries/useInvalidateGetValueMessageLinkedDataQuery'
import ActivityImportsTable from './views/ActivityImportsTable'
import EvidenceTacticsTable from './views/EvidenceTacticsTable'
import IdeationTable from './views/IdeationTable'
import { isActivityImporting, isEvidenceTacticActivity, isIdeationActivity } from './ActivityTable.helpers'
import { useActivityRows } from './useActivityRows'

export const EVIDENCE_TACTICS_TAB_ID = 'evidence-tactics'

export const IDEATION_TAB_ID = 'ideation'

export const ACTIVITY_IMPORTS_TAB_ID = 'imports'

type ActivityTableProps = {
    selectedTab: string
    setSelectedTab: (tabId: string) => void
}

const ActivityTable = ({ selectedTab, setSelectedTab }: ActivityTableProps) => {
    const activitiesDisplayName = useActivitiesDisplayName()

    const { data, isLoading, isError } = useGetActivitiesQuery()

    const allRows = useActivityRows(data)
    const evidenceTactics = useMemo(() => allRows.filter(isEvidenceTacticActivity), [allRows])
    const ideation = useMemo(() => allRows.filter(isIdeationActivity), [allRows])
    const imports = useMemo(() => allRows.filter(isActivityImporting), [allRows])

    const { mutate: reorderActivities } = useReorderActivitiesMutation()
    const { invalidateActivitiesQuery } = useInvalidateActivitiesQuery()
    const { invalidateGetValueMessageLinkedDataQuery } = useInvalidateGetValueMessageLinkedDataQuery()

    const { openRow: openActivity, navigationStack, openPanel, closePanel } = useTableDetailPanel(allRows, 'Activity')

    const tabs = [
        {
            id: EVIDENCE_TACTICS_TAB_ID,
            label: activitiesDisplayName.plural,
            view: (
                <EvidenceTacticsTable
                    rows={evidenceTactics}
                    activitiesLabel={activitiesDisplayName.plural}
                    singularActivityLabel={activitiesDisplayName.singular}
                    onOpenPanel={openPanel}
                    onReorder={reorderActivities}
                />
            ),
        },
        {
            id: IDEATION_TAB_ID,
            label: 'Ideation',
            view: (
                <IdeationTable
                    rows={ideation}
                    singularActivityLabel={activitiesDisplayName.singular}
                    onOpenPanel={openPanel}
                    onReorder={reorderActivities}
                />
            ),
        },
        {
            id: ACTIVITY_IMPORTS_TAB_ID,
            label: 'Imports',
            view: <ActivityImportsTable rows={imports} onOpenPanel={openPanel} />,
        },
    ]

    return (
        <Fade in timeout={800}>
            <Grid container item xs minWidth={0}>
                <DataTableSection
                    isLoading={isLoading}
                    isError={isError}
                    entityLabel={activitiesDisplayName.plural}
                    loadingTitle={activitiesDisplayName.plural}
                >
                    <Tabs
                        tabs={tabs}
                        selectedTabId={selectedTab}
                        onTabChange={setSelectedTab}
                        orientation="horizontal"
                        sx={{ minWidth: 0 }}
                        tabPanelSx={{ pt: 2, minWidth: 0 }}
                    />
                </DataTableSection>
                {!!openActivity && (
                    <TablePanel
                        data={openActivity}
                        type="activity"
                        closePanel={closePanel}
                        openPanelAction
                        updateParent={() => {
                            invalidateActivitiesQuery()
                            invalidateGetValueMessageLinkedDataQuery()
                        }}
                        navigationStack={navigationStack}
                    />
                )}
            </Grid>
        </Fade>
    )
}

export default ActivityTable
